import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { TESTIMONIAL_AVATARS } from "../data/static";

const Stars = ({ count }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < count ? "fill-[#FDE68A] text-[#D1603D]" : "text-slate-200"}`} />
    ))}
  </div>
);

const AVATAR_EMOJIS = {
  t1: "👩🏽",
  t2: "👨🏻",
  t3: "👨‍👩‍👧‍👦",
  t4: "👨🏽",
  t5: "👩🏻",
  t6: "👨🏾"
};

const Card = ({ t }) => (
  <article data-testid={`testimonial-${t.id}`} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-3">
    <div className="h-full bg-white rounded-3xl p-7 sm:p-8 border border-slate-200/80 hover:border-[#D1603D]/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-900/5 transition-all duration-300 flex flex-col">
      <Quote className="w-7 h-7 text-[#D1603D]/60 -ml-1" />
      <p className="mt-4 text-[15px] text-slate-700 leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#FADBD1]/30 flex items-center justify-center text-2xl shadow-sm border border-[#FADBD1]/50">
          {AVATAR_EMOJIS[t.id] || "👤"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm truncate">
            {t.name}<span className="font-normal text-slate-500"> · {t.location}</span>
          </p>
          <p className="text-xs text-slate-500 truncate">{t.trip}</p>
        </div>
        <Stars count={t.rating} />
      </div>
    </div>
  </article>
);

export const Testimonials = () => {
  const { t } = useI18n();
  const section = t.testimonials;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", skipSnaps: false });
  const [selected, setSelected] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [hovering, setHovering] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setSnapCount(emblaApi.scrollSnapList().length);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || hovering) return;
    const id = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => clearInterval(id);
  }, [emblaApi, hovering]);

  return (
    <section id="testimonials" data-testid="testimonials-section"
      className="relative py-20 lg:py-28 bg-white border-y border-slate-200/70"
      onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">{section.eyebrow}</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight">
              {section.title}<br className="hidden sm:block" />
              <span className="italic text-slate-500"> {section.titleItalic}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="testimonials-prev" aria-label={section.prev}
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              className="w-11 h-11 rounded-full border border-slate-200 bg-white text-slate-900 flex items-center justify-center hover:border-[#D1603D] hover:text-[#D1603D] transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="testimonials-next" aria-label={section.next}
              onClick={() => emblaApi && emblaApi.scrollNext()}
              className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-[#D1603D] transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden -mx-3" ref={emblaRef}>
          <div className="flex">
            {section.list.map((entry) => (
              <Card key={entry.id} t={entry} />
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {Array.from({ length: snapCount }).map((_, i) => (
            <button key={i} data-testid={`testimonials-dot-${i}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)} aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${selected === i ? "w-8 bg-[#D1603D]" : "w-1.5 bg-slate-300"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
